import { useState, useEffect } from 'react'
import { useParams, Link, useLocation } from 'react-router-dom'
import { Loader2, ShieldCheck, MapPin, Check, Landmark } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Logo } from '@/shared/components/Logo'
import { cn, notify } from '@/shared/utils'
import { useAccount } from '../hooks/useAccount'
import { accountApi } from '../api/accountApi'
import { AddressAutocomplete } from '@/features/onboarding/components/AddressAutocomplete'

interface LocationForm {
  address: string
  latitude: number | null
  longitude: number | null
  addressLine1: string
  addressLine2: string
  landmark: string
  city: string
  state: string
  postalCode: string
  phoneNumber: string
}

const emptyLocation: LocationForm = {
  address: '',
  latitude: null,
  longitude: null,
  addressLine1: '',
  addressLine2: '',
  landmark: '',
  city: '',
  state: '',
  postalCode: '',
  phoneNumber: '',
}

export function ProfilePage() {
  const { vendorId } = useParams<{ vendorId: string }>()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { account, isLoading, updateProfile, isUpdating } = useAccount()

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')

  const [loc, setLoc] = useState<LocationForm>(emptyLocation)
  const [bank, setBank] = useState({
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    bankName: '',
    upiId: '',
  })

  const { data: savedLocation, isLoading: isLoadingLocation } = useQuery({
    queryKey: ['vendor-location', vendorId],
    queryFn: () => accountApi.getLocation(vendorId!),
    enabled: !!vendorId,
  })

  const updateLocationMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      accountApi.updateLocation(vendorId!, data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-location', vendorId] })
      notify.success('Location updated')
    },
    onError: (error: Error) => {
      notify.error(error.message || 'Unable to update location')
    },
  })

  const { data: savedBank, isLoading: isLoadingBank } = useQuery({
    queryKey: ['vendor-bank', vendorId],
    queryFn: () => accountApi.getBankAccount(vendorId!),
    enabled: !!vendorId,
  })

  const saveBankMutation = useMutation({
    mutationFn: (data: typeof bank) =>
      accountApi.saveBankAccount(vendorId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-bank', vendorId] })
      notify.success('Bank account saved')
    },
    onError: (error: Error) => {
      notify.error(error.message || 'Unable to save bank account')
    },
  })

  useEffect(() => {
    if (savedBank) {
      setBank({
        accountHolderName: savedBank.accountHolderName || '',
        accountNumber: savedBank.accountNumber || '',
        ifscCode: savedBank.ifscCode || '',
        bankName: savedBank.bankName || '',
        upiId: savedBank.upiId || '',
      })
    }
  }, [savedBank])

  useEffect(() => {
    if (account) {
      setName(account.name || '')
      setPhone(account.phone || '')
      setEmail(account.email || '')
    }
  }, [account])

  useEffect(() => {
    if (savedLocation) {
      setLoc({
        address: savedLocation.address || '',
        latitude: savedLocation.latitude,
        longitude: savedLocation.longitude,
        addressLine1: savedLocation.addressLine1 || '',
        addressLine2: savedLocation.addressLine2 || '',
        landmark: savedLocation.landmark || '',
        city: savedLocation.city || '',
        state: savedLocation.state || '',
        postalCode: savedLocation.postalCode || '',
        phoneNumber: savedLocation.phoneNumber || '',
      })
    }
  }, [savedLocation])

  const updateLoc = (field: keyof LocationForm, value: string | number) => {
    setLoc((prev) => ({ ...prev, [field]: value }))
  }

  const handleSave = () => {
    if (name.trim().length < 3) return
    updateProfile({ name: name.trim(), email: email.trim() })
  }

  const handleLocationSave = () => {
    if (!loc.address || loc.latitude === null || loc.longitude === null) {
      notify.error('Please select an address from the suggestions')
      return
    }
    if (!loc.addressLine1.trim()) {
      notify.error('Please enter your shop/stall number or flat details')
      return
    }
    updateLocationMutation.mutate({
      latitude: loc.latitude,
      longitude: loc.longitude,
      address: loc.address,
      label: 'OUTLET',
      addressLine1: loc.addressLine1.trim(),
      addressLine2: loc.addressLine2.trim(),
      landmark: loc.landmark.trim(),
      city: 'Delhi',
      state: 'Delhi',
      postalCode: loc.postalCode.trim(),
      phoneNumber: loc.phoneNumber.trim(),
    })
  }

  const handleBankSave = () => {
    if (!bank.accountHolderName.trim()) { notify.error('Enter account holder name'); return }
    if (!bank.accountNumber.trim() || bank.accountNumber.length < 8) { notify.error('Enter valid account number'); return }
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(bank.ifscCode.toUpperCase())) { notify.error('Enter valid IFSC code (e.g. SBIN0001234)'); return }
    if (!bank.bankName.trim()) { notify.error('Enter bank name'); return }
    saveBankMutation.mutate({ ...bank, ifscCode: bank.ifscCode.toUpperCase() })
  }

  const journeyStarted = account?.createdAt ? new Date(account.createdAt).toLocaleDateString() : '—'

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card/90 border-b sticky top-0 z-20 shadow-sm backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <Logo href={`/dashboard/${vendorId}`} />
              <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
                <Link
                  to={`/dashboard/${vendorId}`}
                  className={cn(
                    'pb-1 transition-colors border-b-2',
                    location.pathname === `/dashboard/${vendorId}`
                      ? 'text-foreground border-foreground'
                      : 'text-muted-foreground border-transparent hover:text-foreground'
                  )}
                >
                  Dashboard
                </Link>
                <Link
                  to={`/inventory/${vendorId}`}
                  className={cn(
                    'pb-1 transition-colors border-b-2',
                    location.pathname === `/inventory/${vendorId}`
                      ? 'text-foreground border-foreground'
                      : 'text-muted-foreground border-transparent hover:text-foreground'
                  )}
                >
                  Inventory
                </Link>
                <Link
                  to={`/orders/${vendorId}`}
                  className={cn(
                    'pb-1 transition-colors border-b-2',
                    location.pathname === `/orders/${vendorId}`
                      ? 'text-foreground border-foreground'
                      : 'text-muted-foreground border-transparent hover:text-foreground'
                  )}
                >
                  Orders
                </Link>
              </nav>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Profile Section */}
        <div className="bg-card border rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Profile</h1>
              <p className="text-sm text-muted-foreground">Keep your details up to date for smoother operations.</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <ShieldCheck className="w-4 h-4" />
              Secure account
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input id="phone" value={phone} placeholder="Enter phone number" disabled />
              <p className="text-xs text-muted-foreground">Phone updates require verification.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                disabled
              />
            </div>
            <div className="space-y-2">
              <Label>Journey Started</Label>
              <div className="rounded-md border px-3 py-2 text-sm text-muted-foreground">{journeyStarted}</div>
            </div>
          </div>

          <div className="flex justify-end mt-8">
            <Button onClick={handleSave} disabled={isUpdating}>
              {isUpdating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save changes'
              )}
            </Button>
          </div>
        </div>

        {/* Outlet Location */}
        <div className="bg-card border rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <MapPin className="w-5 h-5 text-primary" />
            <div>
              <h2 className="text-lg font-semibold text-foreground">Outlet Location</h2>
              <p className="text-sm text-muted-foreground">
                Set your outlet address so customers can order from you.
              </p>
            </div>
          </div>

          {isLoadingLocation ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4">
              {savedLocation && (
                <div className="text-sm text-green-600 bg-green-50 rounded-lg px-3 py-2 space-y-1">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 shrink-0" />
                    <span className="font-medium">Current Location</span>
                  </div>
                  <p className="text-green-700 ml-6">{savedLocation.address}</p>
                  {(savedLocation.addressLine1 || savedLocation.landmark || savedLocation.postalCode || savedLocation.phoneNumber) && (
                    <div className="ml-6 text-green-600/80 text-xs space-y-0.5">
                      {savedLocation.addressLine1 && <p>{savedLocation.addressLine1}{savedLocation.addressLine2 ? `, ${savedLocation.addressLine2}` : ''}</p>}
                      {savedLocation.landmark && <p>Landmark: {savedLocation.landmark}</p>}
                      {savedLocation.postalCode && <p>Pincode: {savedLocation.postalCode}</p>}
                      {savedLocation.phoneNumber && <p>Phone: +91 {savedLocation.phoneNumber}</p>}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label>Search Location</Label>
                <AddressAutocomplete
                  value={loc.address}
                  placeholder="Search for your area or building..."
                  onChange={(val) => updateLoc('address', val)}
                  onSelect={(data) => {
                    setLoc((prev) => ({
                      ...prev,
                      address: data.address,
                      latitude: data.latitude,
                      longitude: data.longitude,
                    }))
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label>Shop / Stall / Floor No. *</Label>
                <Input
                  value={loc.addressLine1}
                  onChange={(e) => updateLoc('addressLine1', e.target.value)}
                  placeholder="e.g. Shop 5, Ground Floor"
                />
              </div>

              <div className="space-y-2">
                <Label>Building / Market Name</Label>
                <Input
                  value={loc.addressLine2}
                  onChange={(e) => updateLoc('addressLine2', e.target.value)}
                  placeholder="e.g. Main Market, Sector 7"
                />
              </div>

              <div className="space-y-2">
                <Label>Landmark</Label>
                <Input
                  value={loc.landmark}
                  onChange={(e) => updateLoc('landmark', e.target.value)}
                  placeholder="e.g. Near Metro Station, Opposite Park"
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Pincode</Label>
                  <Input
                    value={loc.postalCode}
                    onChange={(e) => updateLoc('postalCode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="6 digits"
                    inputMode="numeric"
                  />
                </div>
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input value="Delhi" disabled className="bg-muted" />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>State</Label>
                  <Input value="Delhi" disabled className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>Country</Label>
                  <Input value="India" disabled className="bg-muted" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Contact Phone (for delivery)</Label>
                <div className="relative">
                  <div className="pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 items-center">
                    <span className="text-sm font-medium text-muted-foreground">+91</span>
                  </div>
                  <Input
                    type="tel"
                    inputMode="numeric"
                    value={loc.phoneNumber}
                    onChange={(e) => updateLoc('phoneNumber', e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="10 digit number"
                    className="pl-12"
                  />
                </div>
              </div>

              {loc.latitude !== null && loc.longitude !== null && (
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Latitude</Label>
                    <Input value={loc.latitude.toFixed(6)} disabled className="bg-muted" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Longitude</Label>
                    <Input value={loc.longitude.toFixed(6)} disabled className="bg-muted" />
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleLocationSave}
                  disabled={updateLocationMutation.isPending || loc.latitude === null}
                >
                  {updateLocationMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : savedLocation ? (
                    'Update Location'
                  ) : (
                    'Save Location'
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
        {/* Bank Account Details */}
        <div className="bg-card border rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <Landmark className="w-5 h-5 text-primary" />
            <div>
              <h2 className="text-lg font-semibold text-foreground">Bank Account</h2>
              <p className="text-sm text-muted-foreground">
                Add your bank details for payouts. This is optional but required for receiving online payments.
              </p>
            </div>
          </div>

          {isLoadingBank ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4">
              {savedBank && (
                <div className="text-sm text-green-600 bg-green-50 rounded-lg px-3 py-2 space-y-1">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 shrink-0" />
                    <span className="font-medium">Account Added</span>
                  </div>
                  <p className="text-green-700 ml-6">
                    {savedBank.bankName} — ****{savedBank.accountNumber.slice(-4)}
                  </p>
                </div>
              )}

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Account Holder Name *</Label>
                  <Input
                    value={bank.accountHolderName}
                    onChange={(e) => setBank((p) => ({ ...p, accountHolderName: e.target.value }))}
                    placeholder="As per bank records"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bank Name *</Label>
                  <Input
                    value={bank.bankName}
                    onChange={(e) => setBank((p) => ({ ...p, bankName: e.target.value }))}
                    placeholder="e.g. State Bank of India"
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Account Number *</Label>
                  <Input
                    value={bank.accountNumber}
                    onChange={(e) => setBank((p) => ({ ...p, accountNumber: e.target.value.replace(/\D/g, '').slice(0, 18) }))}
                    placeholder="Enter account number"
                    inputMode="numeric"
                  />
                </div>
                <div className="space-y-2">
                  <Label>IFSC Code *</Label>
                  <Input
                    value={bank.ifscCode}
                    onChange={(e) => setBank((p) => ({ ...p, ifscCode: e.target.value.toUpperCase().slice(0, 11) }))}
                    placeholder="e.g. SBIN0001234"
                    maxLength={11}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>UPI ID (optional)</Label>
                <Input
                  value={bank.upiId}
                  onChange={(e) => setBank((p) => ({ ...p, upiId: e.target.value }))}
                  placeholder="e.g. yourname@upi"
                />
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleBankSave}
                  disabled={saveBankMutation.isPending}
                >
                  {saveBankMutation.isPending ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                  ) : savedBank ? (
                    'Update Bank Details'
                  ) : (
                    'Save Bank Details'
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
